import EditNoteIcon from "@mui/icons-material/EditNote";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { useEffect, useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import { Alert, Autocomplete, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { LoadUsers } from "../../../../app/AppDataLoader";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { CancelButton } from "../../../../app/components/CancelButton";
import { SaveButton } from "../../../../app/components/SaveButton";
import { selectAllUsers } from "../../../users/usersSlice";
import {
    addDocumentTeamMemberToStage,
    DocumentTeamMemberDto,
    DocumentWorkflowDefinitionDto,
    DocumentWorkflowStageDto,
} from "../documentWorkflowApi";
import { getStageUiType } from "../documentWorkflowHelpers";
import { Localized } from "@fluent/react";

type StageType = "EDIT" | "REVIEW" | "APPROVAL";

type WorkflowStageUser = {
    id: number;
    name: string;
    userName: string;
    email: string;
    position: string;
    companyName: string;
};

type AddWorkflowStageMemberDialogProps = {
    documentId?: number | string | null;
    stage: DocumentWorkflowStageDto;
    workflowTeamMembers: DocumentTeamMemberDto[];
    onClose: () => void;
    onSuccess: (definition: DocumentWorkflowDefinitionDto) => void;
};

const stageTypeStyles: Record<StageType, { color: string; border: string; background: string }> = {
    EDIT: { color: "#1976D2", border: "#5FA8E8", background: "#EAF4FF" },
    REVIEW: { color: "#EF6C00", border: "#FFB680", background: "#FFF4E8" },
    APPROVAL: { color: "#2E7D32", border: "#74C37A", background: "#EAF7EA" },
};

const stageTypeChipIcons: Record<StageType, JSX.Element> = {
    EDIT: <EditNoteIcon />,
    REVIEW: <QuestionAnswerIcon />,
    APPROVAL: <TaskAltOutlinedIcon />,
};

const stageTypeLabels: Record<StageType, string> = {
    EDIT: "Opracowanie",
    REVIEW: "Opiniowanie",
    APPROVAL: "Zatwierdzanie",
};

const roleActionLabels: Record<StageType, string> = {
    EDIT: "Dodaj edytora",
    REVIEW: "Dodaj opiniujacego",
    APPROVAL: "Dodaj zatwierdzajacego",
};

const getStageTypeChipSx = (stageType: StageType) => ({
    color: stageTypeStyles[stageType].color,
    borderColor: stageTypeStyles[stageType].border,
    bgcolor: stageTypeStyles[stageType].background,
    borderWidth: 1,
    borderStyle: "solid",
    fontWeight: 600,
    "& .MuiChip-icon": {
        color: "inherit",
    },
});

const fallbackUserIdFromText = (value: string) => {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
        hash = ((hash << 5) - hash) + value.charCodeAt(index);
        hash |= 0;
    }

    // Keep fallback IDs negative to avoid collisions with numeric API IDs.
    return -Math.abs(hash || 1);
};

export const AddWorkflowStageMemberDialog = ({
    documentId,
    stage,
    workflowTeamMembers,
    onClose,
    onSuccess,
}: AddWorkflowStageMemberDialogProps) => {
    const dispatch = useAppDispatch();
    const allUsers = useAppSelector(selectAllUsers);
    const stageId = stage.id;
    const stageName = stage.name;
    const stageType = getStageUiType(stage) as StageType;
    const [selectedUser, setSelectedUser] = useState<WorkflowStageUser | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const usersFromStore = useMemo(() => {
        return allUsers
            .map(user => {
                const numericId = Number(user.id);
                const mappedId = Number.isFinite(numericId)
                    ? numericId
                    : fallbackUserIdFromText(user.userName || user.email || user.id);

                return {
                    id: mappedId,
                    name: `${user.firstname} ${user.surname}`.trim(),
                    userName: user.userName,
                    email: user.email,
                    position: user.position,
                    companyName: user.companyName,
                } as WorkflowStageUser;
            });
    }, [allUsers]);

    const selectableUsers = useMemo(() => {
        const assignedUserNames = new Set(
            workflowTeamMembers
                .filter(member => stage.documentTeamMemberIds.includes(member.id))
                .map(member => member.userName.toLowerCase())
        );

        return usersFromStore
            .filter(user => !assignedUserNames.has(user.userName.toLowerCase()))
            .sort((left, right) => left.name.localeCompare(right.name));
    }, [stage.documentTeamMemberIds, usersFromStore, workflowTeamMembers]);

    useEffect(() => {
        setSelectedUser(null);
        setIsSaving(false);
        setServerError("");
    }, [stage.id]);

    const handleConfirmClick = async () => {
        if (!selectedUser || !documentId || !stageId || isSaving) {
            return;
        }

        setIsSaving(true);
        try {
            const result = await dispatch(
                addDocumentTeamMemberToStage({
                    documentId: String(documentId),
                    stageId: String(stageId),
                    userName: selectedUser.userName,
                })
            );

            if (addDocumentTeamMemberToStage.fulfilled.match(result)) {
                setServerError("");
                onSuccess(result.payload);
            } else {
                let errorMsg = "error";

                if (result.payload && typeof result.payload === "object") {
                    const payload = result.payload as {
                        kind?: string;
                        problem?: { title?: string };
                    };

                    if (payload.kind === "http") {
                        const problem = payload.problem;
                        if (problem?.title) {
                            errorMsg = problem.title;
                        }
                    }
                }

                setServerError(errorMsg);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return <Dialog open={true} fullWidth maxWidth="sm">
        <DialogTitle>{roleActionLabels[stageType]}</DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                        p: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: alpha(stageTypeStyles[stageType].border, 0.9),
                        bgcolor: alpha(stageTypeStyles[stageType].background, 0.9),
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                            Etap:
                        </Typography>
                        <Chip
                            size="small"
                            label={stageTypeLabels[stageType]}
                            icon={stageTypeChipIcons[stageType]}
                            variant="outlined"
                            sx={{ ...getStageTypeChipSx(stageType), borderRadius: 0.75 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {stageName}
                        </Typography>
                    </Box>
                </Box>
                <LoadUsers component={<Skeleton variant="rounded" height={42} />}>
                    {selectableUsers.length === 0
                        ? <Alert severity="info">Brak dostepnych osob do dodania na ten etap.</Alert>
                        : <Autocomplete
                            options={selectableUsers}
                            value={selectedUser}
                            onChange={(_, newValue) => setSelectedUser(newValue)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={option => `${option.name} (${option.userName})`}
                            renderInput={params => <TextField
                                {...params}
                                size="small"
                                label="User"
                                helperText={selectedUser?.position ?? " "}
                            />}
                        />}
                </LoadUsers>

                {
                    serverError && <Alert sx={{ marginTop: 1, }} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                }
            </Stack>
        </DialogContent>
        <DialogActions>
            <SaveButton
                loading={isSaving}
                onClick={handleConfirmClick}
                disabled={
                    selectableUsers.length === 0
                    || !selectedUser
                    || !documentId
                    || !stageId
                    || isSaving
                }
            />
            <CancelButton loading={false} onClick={onClose} disabled={isSaving} />
        </DialogActions>
    </Dialog>;
};
